/**
 * PhoneInput — country-code selector + national number input.
 *
 * The Figma shows a flag + dial-code dropdown beside a number input.
 * The current backend takes a single `mobile` string (already includes
 * country code per existing validation). We compose the two visually
 * but submit `${dialCode}${number}` joined when caller asks for it.
 *
 * Controlled via:
 *   - countryId / onCountryChange  (country.id from the countries endpoint)
 *   - value / onChange              (the national-number string)
 *
 * `countries` array shape: [{ id, name, phone_code, iso_code? }]
 *
 * Helpful render: shows flag emoji computed from iso_code (if available)
 * + dial code. Falls back to dial code only when iso_code is missing.
 */

import { useMemo } from 'react'
import { HiOutlineChevronDown } from 'react-icons/hi2'

function flagEmoji(iso) {
  if (!iso || iso.length !== 2) return ''
  const A = 0x1F1E6
  const base = 'A'.charCodeAt(0)
  return String.fromCodePoint(
    A + (iso.toUpperCase().charCodeAt(0) - base),
    A + (iso.toUpperCase().charCodeAt(1) - base),
  )
}

export default function PhoneInput({
  countries = [],
  countryId,
  onCountryChange,
  value,
  onChange,
  invalid = false,
  tone = 'default',
  placeholder = 'Phone number',
  loadingCountries = false,
  className = '',
}) {
  const selected = useMemo(
    () => countries.find((c) => String(c.id) === String(countryId)),
    [countries, countryId]
  )

  const toneCls = tone === 'glass'
    ? 'bg-white/95 dark:bg-white/10 border-white/70 dark:border-white/15 backdrop-blur text-ink-primary shadow-token-sm'
    : 'bg-surface border-border text-ink-primary'
  const errorCls = invalid ? 'border-danger' : ''

  return (
    <div className={`flex items-stretch gap-2 ${className}`}>
      {/* Country code chip */}
      <div className={`relative inline-flex items-center rounded-lg border ${toneCls} ${errorCls} ps-2 pe-7`}>
        {selected?.iso_code && (
          <span className="text-base me-1.5 leading-none" aria-hidden="true">
            {flagEmoji(selected.iso_code)}
          </span>
        )}
        <span className="text-sm font-medium text-ink-primary whitespace-nowrap">
          {selected?.phone_code || '+--'}
        </span>
        <select
          aria-label="Country code"
          value={countryId || ''}
          onChange={(e) => onCountryChange?.(e.target.value)}
          disabled={loadingCountries}
          className="absolute inset-0 w-full opacity-0 cursor-pointer"
        >
          <option value="">Select</option>
          {countries.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name} ({c.phone_code})
            </option>
          ))}
        </select>
        <HiOutlineChevronDown className="absolute end-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-ink-muted pointer-events-none" />
      </div>

      {/* National number */}
      <input
        type="tel"
        inputMode="tel"
        autoComplete="tel-national"
        value={value || ''}
        onChange={(e) => onChange?.(e.target.value)}
        placeholder={placeholder}
        aria-invalid={invalid || undefined}
        className={`
          flex-1 h-10 rounded-lg border px-3 text-sm
          placeholder:text-ink-muted transition-colors
          focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand
          ${toneCls} ${errorCls}
        `}
      />
    </div>
  )
}

// Helper for callers that want the joined digits-only string for submission.
// The backend's mobile validator rejects non-digit characters, so we strip the
// leading "+" from the country dial code (e.g. "+971" → "971") before
// concatenating with the national number's digits. Result: pure digits, ready
// for the backend without triggering "mobile must be numeric" validation.
PhoneInput.joinE164 = function joinE164(dialCode, national) {
  if (!dialCode || !national) return String(national || '').replace(/[^\d]/g, '')
  const dialDigits = String(dialCode).replace(/[^\d]/g, '')
  const nationalDigits = String(national).replace(/[^\d]/g, '')
  return `${dialDigits}${nationalDigits}`
}
